import { ReactionSetRequestDTO } from './models/ReactionSetRequestDTO';
import { Injectable } from "@angular/core";
import { Comment } from "./models/order";
import { environment } from "../../environments/environment";
import { HttpParams, HttpClient } from "@angular/common/http";
import { BBResponseContainer } from "../carrier/response-container";
import { Subject, Observable } from "rxjs";
import { OrderStatusUpdateRequest } from "./models/order-status-update-request";
import { OrderPrintBarcode } from "./models/orderPrintBarcode";
import { tap, timeout } from 'rxjs/operators';
import { OrderCommentsUpdateRequest } from './models/order-comments-update-request';
import { CancelOrderItemRequest } from './models/cancelOrderItemRequest';
import { RushFlagUpdateRequest } from './models/RushFlagUpdateRequest';
import { QCOverRuleUpdateRequest } from './models/QcOverruleUpdateRequest';
import { NxpUkUpdateRequest } from "./models/NxpUkUpdateRequest";
import { DxPUpdateRequest } from "./models/DxPUpdateRequest";

@Injectable()
export class OrderListService {

    readonly ORDER_UPDATE_SERVICE = 'order';

    constructor(private http: HttpClient) { }

    getOrders(start: number, end: number, queryString: string, selectedFilter: string, searchedFilterBarcode: string) {

        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order';

        let requestParam = new HttpParams().set('start', String(start))
            .set('end', String(end)).set('queryString', queryString).set('selectedFilter', selectedFilter).set('searchedFilterBarcode', searchedFilterBarcode);

        const options = url ? { params: requestParam } : {};

        return this.http.get<BBResponseContainer>(url, options).pipe(tap(event => { }, error => {
            error.url = url;
        }));
    }

    countOrder(queryString: string) {

        let orderCountUrl = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/count';
        let requestParam = new HttpParams().set('queryString', queryString);
        const options = orderCountUrl ? { params: requestParam } : {};

        return this.http.get<BBResponseContainer>(orderCountUrl, options).pipe(tap(event => { }, error => {
            error.orderCountUrl = orderCountUrl;
        }));

    }

    // service method for order update
    updateOrder(updateRequest: OrderStatusUpdateRequest) {
        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + this.ORDER_UPDATE_SERVICE;
        return this.http.put<BBResponseContainer>(url, updateRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = url;
        }));
    }

    getOrderView(orderId: string) {
        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/detail';
        let requestParam = new HttpParams().set('salesOrderId', orderId);

        const options = url ? { params: requestParam } : {};
        return this.http.get<BBResponseContainer>(url, options).pipe(tap(event => { }, error => {
            error.url = url;
        }));
    }


    /**
     * @description Generate and print barcodes.
     * @param orderPrintBarcodeRequest
    */
    getGeneratedBarcode(orderPrintBarcodeRequest: OrderPrintBarcode): Observable<BBResponseContainer> {

        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/barcode';
        return this.http.post<BBResponseContainer>(url, orderPrintBarcodeRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = url;
        }));
    }

    /**
     * @description Validate input barcode.
     * @param orderId and input barcode
    */
    validateInputBarcode(orderPrintBarcodeRequest: OrderPrintBarcode): Observable<BBResponseContainer> {

        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/validate/barcode';
        return this.http.post<BBResponseContainer>(url, orderPrintBarcodeRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = url;
        }));
    }

    /**
     * @description Generate and print barcodes.
     * @param orderPrintBarcodeRequest
    */
    printBarcode(orderPrintBarcodeRequest: OrderPrintBarcode): Observable<BBResponseContainer> {

        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/barcode/print';
        return this.http.post<BBResponseContainer>(url, orderPrintBarcodeRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = url;
        }));
    }

    updateComments(comments: Comment[]): Observable<BBResponseContainer> {
        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/order-item/comments';
        return this.http.put<BBResponseContainer>(url, comments).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = url;
        }));
    }

    updateSupportComments(commentUpdateRequest: OrderCommentsUpdateRequest) {
        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/support/comments';
        return this.http.put<BBResponseContainer>(url, commentUpdateRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = url;
        }));
    }

    fetchAllPlatesForAnOrder(orderID: string, genomicsOrderRef: string, productionOrderIds: Array<string>): Observable<BBResponseContainer> {
        let plateUsageurl = environment.BB_SEQUENCING_SERVICE_URL + 'plateUsageOrder';
        let productionOrderId = productionOrderIds.join(",");

        let requestParam = new HttpParams().set('orderId', orderID)
            .set('genomicsOrderRef', genomicsOrderRef)
            .set('productionOrderIds', productionOrderId);

        const options = plateUsageurl ? { params: requestParam } : {};
        return this.http.get<BBResponseContainer>(plateUsageurl, options).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = plateUsageurl;
        }));
    }

    cancelMaterial(updateRequest: CancelOrderItemRequest) {
        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + this.ORDER_UPDATE_SERVICE + "/cancelMaterial";
        return this.http.put<BBResponseContainer>(url, updateRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = url;
        }));
    }

    updateRushFlag(labId: string, rushFlagUpdateRequest: RushFlagUpdateRequest) {
        let rushUrl = environment.BB_ORDERRECEPTION_SERVICE_URL + this.ORDER_UPDATE_SERVICE + '/rush/' + labId;
        return this.http.patch<BBResponseContainer>(rushUrl, rushFlagUpdateRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = rushUrl;
        }));
    }

    updateQcOverRuleFlag(labId: string, qcOverRuleUpdateRequest: QCOverRuleUpdateRequest) {
        let qcUrl = environment.BB_ORDERRECEPTION_SERVICE_URL + this.ORDER_UPDATE_SERVICE + '/qcOverRule/' + labId;
        return this.http.put<BBResponseContainer>(qcUrl, qcOverRuleUpdateRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = qcUrl;
        }));
    }
    updateDxpFlag(labId: string, dxpUpdateRequest: DxPUpdateRequest) {
        let dxpUrl = environment.BB_ORDERRECEPTION_SERVICE_URL + this.ORDER_UPDATE_SERVICE + '/dxp/' + labId;
        return this.http.patch<BBResponseContainer>(dxpUrl, dxpUpdateRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = dxpUrl;
        }));
    }
    updateNxpUkFlag(labId: string, nxpUkUpdateRequest: NxpUkUpdateRequest) {
        let nxpUrl = environment.BB_ORDERRECEPTION_SERVICE_URL + this.ORDER_UPDATE_SERVICE + '/nxpUk/' + labId;
        return this.http.patch<BBResponseContainer>(nxpUrl, nxpUkUpdateRequest).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
            error.url = nxpUrl;
        }));
    }

    getMaterialsForOrder(genomicsOrderRef: string) {
        let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/material/' + genomicsOrderRef;
        return this.http.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
    }

    //shared Methods
    // Observable string sources
    private emitChangeSource = new Subject<any>();
    // Observable string streams
    changeEmitted$ = this.emitChangeSource.asObservable();
    // Service message commands
    emitChange(change: any) {
        console.log("emit change");
        this.emitChangeSource.next(change);
    }

  createReactionSet( reactionSetRequestDTO:ReactionSetRequestDTO, orderId: string) {
      let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/reactionset/'+orderId;
      return this.http.put<BBResponseContainer>(url, reactionSetRequestDTO).pipe(timeout(environment.defaultTimeout));
  }
  getAllSamplesAndPrimersForOrder(orderId: string) {
    let url = environment.BB_ORDERRECEPTION_SERVICE_URL + 'order/samples/primers/'+orderId;
    return this.http.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
  }
}
